import { Head, Link, router } from '@inertiajs/react'
import { useState } from 'react'
import { ArrowLeft, Plus, Pencil, Trash2, X, Users } from 'lucide-react'
import NavigationRail from '@/components/NavigationRail'
import SettingsNav from '@/components/SettingsNav'
import ConfirmDialog from '@/components/ConfirmDialog'
import type { Agent } from '@/types/shared'

interface TeamData {
  id: number
  name: string
  emoji: string | null
  channels: string[]
  member_ids: number[]
  members_count: number
  open_conversations_count: number
}

interface TeamsProps {
  teams: TeamData[]
  agents: Agent[]
  assignedChannels: Record<string, number>
  currentAgent: Agent
}

const CHANNEL_LABELS: Record<string, string> = {
  chat: 'Chat',
  email: 'Email',
  whatsapp: 'WhatsApp',
}

const CHANNEL_EMOJIS: Record<string, string> = {
  chat: '💬',
  email: '📧',
  whatsapp: '📱',
}

interface FormData {
  name: string
  emoji: string
  channels: string[]
  member_ids: number[]
}

const EMPTY_FORM: FormData = {
  name: '',
  emoji: '',
  channels: [],
  member_ids: [],
}

export default function Teams({ teams, agents, assignedChannels, currentAgent }: TeamsProps) {
  const [showModal, setShowModal] = useState(false)
  const [editingTeam, setEditingTeam] = useState<TeamData | null>(null)
  const [form, setForm] = useState<FormData>(EMPTY_FORM)
  const [processing, setProcessing] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<TeamData | null>(null)

  const openCreate = () => {
    setEditingTeam(null)
    setForm(EMPTY_FORM)
    setShowModal(true)
  }

  const openEdit = (team: TeamData) => {
    setEditingTeam(team)
    setForm({
      name: team.name,
      emoji: team.emoji || '',
      channels: team.channels,
      member_ids: team.member_ids,
    })
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingTeam(null)
    setForm(EMPTY_FORM)
  }

  const toggleChannel = (channel: string) => {
    setForm((prev) => ({
      ...prev,
      channels: prev.channels.includes(channel)
        ? prev.channels.filter((c) => c !== channel)
        : [...prev.channels, channel],
    }))
  }

  const toggleMember = (agentId: number) => {
    setForm((prev) => ({
      ...prev,
      member_ids: prev.member_ids.includes(agentId)
        ? prev.member_ids.filter((id) => id !== agentId)
        : [...prev.member_ids, agentId],
    }))
  }

  const isChannelDisabled = (channel: string) => {
    // Channel is disabled if assigned to another team (not the one being edited)
    const assignedTo = assignedChannels[channel]
    if (!assignedTo) return false
    if (editingTeam && assignedTo === editingTeam.id) return false
    return true
  }

  const channelAssignedToTeamName = (channel: string) => {
    const assignedTo = assignedChannels[channel]
    if (!assignedTo) return null
    if (editingTeam && assignedTo === editingTeam.id) return null
    const team = teams.find((t) => t.id === assignedTo)
    return team?.name || null
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) return
    setProcessing(true)

    const payload = {
      name: form.name.trim(),
      emoji: form.emoji.trim() || null,
      channels: form.channels,
      member_ids: form.member_ids,
    }

    if (editingTeam) {
      router.patch(`/settings/teams/${editingTeam.id}`, payload, {
        onSuccess: () => closeModal(),
        onFinish: () => setProcessing(false),
      })
    } else {
      router.post('/settings/teams', payload, {
        onSuccess: () => closeModal(),
        onFinish: () => setProcessing(false),
      })
    }
  }

  const handleDelete = (team: TeamData) => {
    setDeleteConfirm(team)
  }

  const confirmDelete = () => {
    if (!deleteConfirm) return
    router.delete(`/settings/teams/${deleteConfirm.id}`, {
      onSuccess: () => setDeleteConfirm(null),
    })
  }

  return (
    <>
      <Head title="Equipes" />
      <div className="h-screen flex bg-gray-50">
        <NavigationRail currentAgent={currentAgent} activePage="settings" />

        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-14 bg-white border-b border-gray-200 flex items-center px-6">
            <Link
              href="/inbox"
              className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm mr-4"
            >
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <h1 className="text-lg font-semibold text-gray-900">Configurações</h1>
          </header>

          <div className="flex-1 flex min-h-0">
            <SettingsNav activePage="teams" isAdmin={currentAgent.role === 'admin'} />

            <main className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-6 py-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-xl font-bold text-gray-900">Equipes</h1>
                <p className="text-sm text-gray-500 mt-1">
                  Organize seus agentes em equipes e configure quais canais cada equipe gerencia.
                </p>
              </div>
              <button
                type="button"
                onClick={openCreate}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                Nova Equipe
              </button>
            </div>

            {teams.length === 0 ? (
              <div className="text-center bg-white rounded-xl border border-gray-200" style={{ padding: '120px 24px' }}>
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-sm font-medium text-gray-900 mb-1">Nenhuma equipe criada</h3>
                <p className="text-sm text-gray-500 mb-6">
                  Crie equipes para organizar seus agentes e rotear conversas automaticamente.
                </p>
                <button
                  type="button"
                  onClick={openCreate}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
                >
                  <Plus className="w-4 h-4" />
                  Criar Equipe
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Equipe</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Canais</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Membros</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Conversas Abertas</th>
                      <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teams.map((team) => (
                      <tr key={team.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50 group">
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => openEdit(team)}
                            className="flex items-center gap-2 cursor-pointer hover:text-blue-600 transition-colors"
                          >
                            <span className="text-lg">{team.emoji || '👥'}</span>
                            <span className="font-medium text-sm text-gray-900 group-hover:text-blue-600">{team.name}</span>
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1.5 flex-wrap">
                            {team.channels.length > 0 ? team.channels.map((ch) => (
                              <span key={ch} className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-full">
                                {CHANNEL_EMOJIS[ch]} {CHANNEL_LABELS[ch]}
                              </span>
                            )) : (
                              <span className="text-xs text-gray-400">Nenhum canal</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {team.members_count}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {team.open_conversations_count}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              type="button"
                              onClick={() => openEdit(team)}
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                              title="Editar"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(team)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                              title="Excluir"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
            </main>
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/40" onClick={closeModal} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 max-h-[70vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900">
                {editingTeam ? 'Editar Equipe' : 'Nova Equipe'}
              </h2>
              <button
                type="button"
                onClick={closeModal}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {/* Name */}
              <div>
                <label htmlFor="team-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Nome da Equipe
                </label>
                <input
                  id="team-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ex: Suporte, Vendas..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  autoFocus
                  required
                />
              </div>

              {/* Emoji */}
              <div>
                <label htmlFor="team-emoji" className="block text-sm font-medium text-gray-700 mb-1">
                  Emoji (opcional)
                </label>
                <input
                  id="team-emoji"
                  type="text"
                  value={form.emoji}
                  onChange={(e) => setForm({ ...form, emoji: e.target.value })}
                  placeholder="Ex: 💬, 📧, 🎯..."
                  className="w-20 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center"
                  maxLength={2}
                />
              </div>

              {/* Channels */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Canais
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Conversas destes canais serão automaticamente atribuídas a esta equipe.
                </p>
                <div className="space-y-2">
                  {Object.keys(CHANNEL_LABELS).map((ch) => {
                    const disabled = isChannelDisabled(ch)
                    const assignedTo = channelAssignedToTeamName(ch)
                    return (
                      <label
                        key={ch}
                        className={`flex items-center gap-2.5 px-3 py-1.5 rounded-lg border ${
                          disabled
                            ? 'border-gray-100 bg-gray-50 cursor-not-allowed'
                            : form.channels.includes(ch)
                              ? 'border-blue-200 bg-blue-50 cursor-pointer'
                              : 'border-gray-200 hover:bg-gray-50 cursor-pointer'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={form.channels.includes(ch)}
                          onChange={() => toggleChannel(ch)}
                          disabled={disabled}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm">{CHANNEL_EMOJIS[ch]}</span>
                        <span className={`text-sm ${disabled ? 'text-gray-400' : 'text-gray-700'}`}>
                          {CHANNEL_LABELS[ch]}
                        </span>
                        {assignedTo && (
                          <span className="text-xs text-gray-400 ml-auto">
                            Atribuído a &quot;{assignedTo}&quot;
                          </span>
                        )}
                      </label>
                    )
                  })}
                </div>
              </div>

              {/* Members */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Membros
                </label>
                <div className="space-y-1 max-h-32 overflow-y-auto border border-gray-200 rounded-lg p-2">
                  {agents.map((agent) => (
                    <label
                      key={agent.id}
                      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md cursor-pointer ${
                        form.member_ids.includes(agent.id)
                          ? 'bg-blue-50'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={form.member_ids.includes(agent.id)}
                        onChange={() => toggleMember(agent.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{agent.name}</span>
                      <span className="text-xs text-gray-400">{agent.email}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-1">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={processing || !form.name.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors cursor-pointer"
                >
                  {processing ? 'Salvando...' : editingTeam ? 'Salvar' : 'Criar Equipe'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteConfirm}
        onOpenChange={(open) => { if (!open) setDeleteConfirm(null) }}
        onConfirm={confirmDelete}
        title="Excluir equipe"
        description={`Tem certeza que deseja excluir a equipe "${deleteConfirm?.name}"? As conversas desta equipe ficarão sem equipe atribuída.`}
        confirmLabel="Excluir"
        variant="destructive"
      />
    </>
  )
}
